import { z } from 'zod';

import { boolParam, listQuerySchema } from './common.js';
import type { ProductionDossier } from './production.js';

/**
 * MARKA KİTİ + MARKA HAFIZASI — müşterinin markası (Dalga 5, 2 Eki 2026).
 *
 * 🔴 `brand.ts` İLE KARIŞTIRMA: o dosya MENTORIVO'nun kendi logosu (PDF dışa
 * aktarımının satır içi SVG'si). Bu dosya MÜŞTERİNİN markası — reklamlarına
 * giren ton, renk, font, logo, son kart ve AI'ın onun hakkında öğrendikleri.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Kullanıcı kararı (KREATIF-STUDYO-PLANI.md §4, 2 Eki 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 * Kreatif stüdyosunda ayrı **"Marka"** sayfası (Geçmiş ve Şablonlar'ın yanında):
 * üstte KİT (ton, renk, font, logo, son kart CTA'sı, yap/yapma), altta AI
 * NOTLARI (gör, sil, sabitle); sohbetten de yönetilir.
 *
 *   BrandKit         çalışma alanı başına TEK satır — kullanıcının beyanı.
 *                    Prodüksiyonun ANLA adımı (`dossier.brand`) bunu okur;
 *                    son kart ve görsel kampanyanın tipografisi renk/fontu
 *                    buradan alır (ürün öne çıkmıyorsa ürün paleti kazanır).
 *   BrandMemoryNote  AI'ın (ve kullanıcının) markaya dair kısa notları:
 *                    beğenilen/reddedilen, kazanan reklam, kural, kaçınılacak,
 *                    öğrenilen. Kaynak kullanıcı, prodüksiyon (onay/revizyon),
 *                    performans (metrik geri beslemesi) ya da sohbet ajanı.
 *
 * İkisi de KİRACI modeli (API `TENANT_MODELS` + `TENANT_DELEGATES`, worker
 * `TENANT_MODELS`): satırlar müşterinin stratejisini taşıyor; kapsamsız okuma
 * başka kiracının marka sesini modele okuturdu.
 *
 * 🔴 Not metni modele GÜVENİLMEZ VERİ olarak gider (kullanıcının ya da
 * sohbetin yazdığı serbest metin — talimat değil, alıntı); prompt API
 * yanıtına girmez. Sağlık/finans iddiası taşıyan not politika denetiminden
 * kaçmaz: notlar dosyaya girer, senaryo/kare planı yine denetlenir.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Font kataloğu — worker'ın deposundaki ailelerin AYNASI
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Markanın seçebileceği fontlar — worker `apps/worker/src/media/fonts.ts`
 * (`FONT_FAMILIES`) kataloğunun aynası; yazı deterministik basıldığı için
 * yalnız depodaki OFL dosyaları seçilebilir (başka bir ad son kartı HATASIZ
 * DejaVuSans'a düşürürdü — fonts.ts başlığı). İki liste worker spec'iyle
 * (`fonts.spec.ts` → "marka kiti kataloğu") eşit tutuluyor; yeni aile önce
 * depoya, sonra buraya.
 *
 *   `lira: false`   ₺ glifi yok — fiyat bu aileyle basılacaksa "TL" yazılır
 *   `upperOnly`     yalnız büyük harf (Bebas Neue) — metin dil kuralıyla büyütülür
 */
export const BRAND_FONTS = [
  { id: 'inter', family: 'Inter', role: 'sans', lira: true, upperOnly: false },
  { id: 'manrope', family: 'Manrope', role: 'sans', lira: true, upperOnly: false },
  { id: 'montserrat', family: 'Montserrat', role: 'sans', lira: true, upperOnly: false },
  { id: 'dm-sans', family: 'DM Sans', role: 'sans', lira: true, upperOnly: false },
  { id: 'space-grotesk', family: 'Space Grotesk', role: 'sans', lira: true, upperOnly: false },
  { id: 'oswald', family: 'Oswald', role: 'condensed', lira: true, upperOnly: false },
  { id: 'bebas-neue', family: 'Bebas Neue', role: 'condensed', lira: false, upperOnly: true },
  {
    id: 'dm-serif-display',
    family: 'DM Serif Display',
    role: 'display',
    lira: false,
    upperOnly: false,
  },
  { id: 'fraunces', family: 'Fraunces', role: 'display', lira: true, upperOnly: false },
  {
    id: 'cormorant-garamond',
    family: 'Cormorant Garamond',
    role: 'serif',
    lira: true,
    upperOnly: false,
  },
  { id: 'jetbrains-mono', family: 'JetBrains Mono', role: 'mono', lira: false, upperOnly: false },
] as const;
export type BrandFontId = (typeof BRAND_FONTS)[number]['id'];
export const BRAND_FONT_IDS = BRAND_FONTS.map((font) => font.id) as readonly BrandFontId[];
export const brandFontIdSchema = z.enum(
  BRAND_FONT_IDS as unknown as readonly [BrandFontId, ...BrandFontId[]],
);

// ═══════════════════════════════════════════════════════════════════════════
// Marka kiti
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Rengin görevi — son kart ve görsel kampanyanın tipografisi rolü okur:
 *   primary     ana marka rengi (CTA düğmesi, vurgu)
 *   secondary   ikinci renk
 *   accent      küçük vurgular (rozet, çizgi) — birden çok olabilir
 *   background  zemin
 *   text        metin
 * primary / background / text en fazla BİRER tane.
 */
export const BRAND_COLOR_ROLES = ['primary', 'secondary', 'accent', 'background', 'text'] as const;
export type BrandColorRole = (typeof BRAND_COLOR_ROLES)[number];
const SINGLE_COLOR_ROLES: readonly BrandColorRole[] = ['primary', 'background', 'text'];

export const BRAND_KIT_LIMITS = {
  name: 80,
  tagline: 120,
  tone: 600,
  /// Yap/yapma kuralının uzunluğu ve listesinin boyu.
  voiceRule: 120,
  voiceRules: 10,
  colors: 8,
  cta: 40,
  musicTaste: 240,
} as const;

/// `#RRGGBB` — büyük harfe çevrilir (aynı renk iki yazımla iki satır olmasın).
export const brandHexSchema = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Renk #RRGGBB biçiminde olmalı.')
  .transform((hex) => hex.toUpperCase());

export const brandColorSchema = z.object({
  hex: brandHexSchema,
  role: z.enum(BRAND_COLOR_ROLES),
});
export type BrandColor = z.infer<typeof brandColorSchema>;

const voiceRuleSchema = z.string().trim().min(1).max(BRAND_KIT_LIMITS.voiceRule);
const nullableText = (max: number) => z.string().trim().min(1).max(max).nullable();

const brandColorsSchema = z
  .array(brandColorSchema)
  .max(BRAND_KIT_LIMITS.colors)
  .superRefine((colors, ctx) => {
    for (const role of SINGLE_COLOR_ROLES) {
      if (colors.filter((color) => color.role === role).length > 1) {
        ctx.addIssue({ code: 'custom', message: `"${role}" rolünde en fazla bir renk olabilir.` });
      }
    }
    if (new Set(colors.map((color) => `${color.role}:${color.hex}`)).size !== colors.length) {
      ctx.addIssue({ code: 'custom', message: 'Aynı renk aynı rolde iki kez olamaz.' });
    }
  });

/// Görsel kampanya ve son kartın fontları — başlık (display) ve gövde (body).
export const brandFontsSchema = z.object({
  display: brandFontIdSchema.nullable(),
  body: brandFontIdSchema.nullable(),
});
export type BrandFonts = z.infer<typeof brandFontsSchema>;

/// Son kart varsayılanları — prodüksiyon isteğinin `endCard`ı verilmezse bunlar.
export const brandEndCardSchema = z.object({
  cta: nullableText(BRAND_KIT_LIMITS.cta),
  showPrice: z.boolean(),
});
export type BrandEndCard = z.infer<typeof brandEndCardSchema>;

/**
 * MARKA KİTİNİN İÇERİĞİ — DB satırının (`brand_kits`) alan alan karşılığı ve
 * `PUT`un gövdesi. Boş kit (hiç kaydedilmemiş) `EMPTY_BRAND_KIT`.
 *
 * `logoId` = `BrandLogo.id` — 🔴 platformun kendi logosu (`isPlatformMark`)
 * REDDEDİLİR; logo yalnız açıkça seçilir (plan §6b: "marka logosu yoksa logo
 * yok"). Silinen logo kitten düşer (`SetNull`).
 */
export const brandKitSchema = z.object({
  name: nullableText(BRAND_KIT_LIMITS.name),
  tagline: nullableText(BRAND_KIT_LIMITS.tagline),
  /// Markanın sesi, kullanıcının cümleleriyle ("samimi ama abartısız, hep 'siz'").
  tone: nullableText(BRAND_KIT_LIMITS.tone),
  voiceDo: z.array(voiceRuleSchema).max(BRAND_KIT_LIMITS.voiceRules),
  voiceDont: z.array(voiceRuleSchema).max(BRAND_KIT_LIMITS.voiceRules),
  colors: brandColorsSchema,
  fonts: brandFontsSchema,
  logoId: z.uuid().nullable(),
  endCard: brandEndCardSchema,
  /// Müzik zevki ("akustik, sakin; EDM yok") — müzik yönünün girdisi, dikte değil.
  musicTaste: nullableText(BRAND_KIT_LIMITS.musicTaste),
});
export type BrandKit = z.infer<typeof brandKitSchema>;

export const EMPTY_BRAND_KIT: BrandKit = {
  name: null,
  tagline: null,
  tone: null,
  voiceDo: [],
  voiceDont: [],
  colors: [],
  fonts: { display: null, body: null },
  logoId: null,
  endCard: { cta: null, showPrice: true },
  musicTaste: null,
};

/**
 * KISMİ GÜNCELLEME (`PATCH /brand/kit`, sohbet aracı) — verilen alanlar BÜTÜNÜYLE
 * değişir (diziler dahil; "bir renk ekle" diyen istemci listenin tamamını
 * gönderir), verilmeyenler korunur. `updatedAt` iyimser eşzamanlılık: verilirse
 * ve satır bu arada değiştiyse 409 (iki sekme / sayfa + sohbet).
 */
export const brandKitPatchSchema = brandKitSchema
  .partial()
  .extend({ updatedAt: z.iso.datetime().optional() })
  .refine(
    (patch) =>
      Object.keys(patch).some(
        (key) => key !== 'updatedAt' && patch[key as keyof typeof patch] !== undefined,
      ),
    { message: 'En az bir alan değişmeli.' },
  );
export type BrandKitPatchInput = z.infer<typeof brandKitPatchSchema>;

/// `GET /brand/kit` — kit + logonun ekran hâli. Hiç kaydedilmemişse boş kit, `updatedAt: null`.
export interface BrandKitView extends BrandKit {
  logo: { id: string; name: string; url: string | null } | null;
  updatedAt: string | null;
  updatedBy: { id: string; name: string | null } | null;
}

/**
 * Marka uçlarının hata kodları — ekrana çıkan i18n anahtarının son parçası
 * (`ProductionErrorCode` deseni). Uçlar ve sohbet aracı bunları döndürür.
 */
export const BrandErrorCode = {
  /// Seçilen logo bu çalışma alanında yok.
  LOGO_NOT_FOUND: 'BRAND_LOGO_NOT_FOUND',
  /// Platformun kendi işareti (`isPlatformMark`) müşteri kitine giremez.
  PLATFORM_LOGO: 'BRAND_PLATFORM_LOGO',
  /// `updatedAt` bayat — kit bu arada değişti (iki sekme / sayfa + sohbet).
  KIT_STALE: 'BRAND_KIT_STALE',
  NOTE_NOT_FOUND: 'BRAND_MEMORY_NOTE_NOT_FOUND',
  /// Sabitlenmiş not tavanı (`BRAND_MEMORY_LIMITS.pinned`).
  PIN_LIMIT: 'BRAND_MEMORY_PIN_LIMIT',
  /// Notun dayanağı (prodüksiyon/kreatif) bu çalışma alanında yok.
  NOTE_REF_INVALID: 'BRAND_MEMORY_REF_INVALID',
} as const;
export type BrandErrorCode = (typeof BrandErrorCode)[keyof typeof BrandErrorCode];

// ═══════════════════════════════════════════════════════════════════════════
// Marka hafızası
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Notun TÜRÜ:
 *   PREFERENCE  beğeni ("sıcak ışık seviyor", "ürünü elde göstermeyi tercih ediyor")
 *   REJECTED    reddedilen ("komedi tonunu istemedi", reddedilen fikir/açılış)
 *   WINNER      kazanan reklam/açılış/biçim (performans ya da kullanıcı)
 *   RULE        kesin kural ("logoyu asla kırpma", "fiyat her zaman TL")
 *   AVOID       kaçınılacak ("rakip adı yok", "kırmızı zemin yok")
 *   LEARNING    öğrenilen (ölçülen: "ilk 2 sn ürünlü açılış izlenme %40 artırdı")
 */
export const BrandMemoryKind = {
  PREFERENCE: 'PREFERENCE',
  REJECTED: 'REJECTED',
  WINNER: 'WINNER',
  RULE: 'RULE',
  AVOID: 'AVOID',
  LEARNING: 'LEARNING',
} as const;
export type BrandMemoryKind = (typeof BrandMemoryKind)[keyof typeof BrandMemoryKind];
export const BRAND_MEMORY_KINDS: readonly BrandMemoryKind[] = Object.values(BrandMemoryKind);
export const brandMemoryKindSchema = z.enum(BrandMemoryKind);

/**
 * Notun KAYNAĞI — yazan taraf sunucuda belirlenir, istemci seçemez:
 *   USER         Marka sayfasında kullanıcı yazdı
 *   PRODUCTION   prodüksiyonun bir kararından türedi (fikir seçimi, revizyon, ret)
 *   PERFORMANCE  reklam metriklerinden (video izlenme, dönüşüm — worker)
 *   AGENT        sohbet ajanı kullanıcının söylediğinden yazdı
 */
export const BrandMemorySource = {
  USER: 'USER',
  PRODUCTION: 'PRODUCTION',
  PERFORMANCE: 'PERFORMANCE',
  AGENT: 'AGENT',
} as const;
export type BrandMemorySource = (typeof BrandMemorySource)[keyof typeof BrandMemorySource];
export const BRAND_MEMORY_SOURCES: readonly BrandMemorySource[] = Object.values(BrandMemorySource);
export const brandMemorySourceSchema = z.enum(BrandMemorySource);

export const BRAND_MEMORY_LIMITS = {
  /// Notun uzunluğu (DB `VARCHAR(500)`).
  text: 500,
  /// Çalışma alanı başına not tavanı — aşınca en eski SABİTLENMEMİŞ ve KURAL
  /// olmayan not düşer (yazıcının işi); sabitlenmiş notlar tavana sayılır ama düşmez.
  perWorkspace: 300,
  /// Sabitlenebilecek not sayısı.
  pinned: 30,
  /// Ağırlık aralığı — dosyaya seçimde sıralama (sabitlenmiş önce, sonra ağırlık).
  /// `machineMax`: prodüksiyon kararlarından türeyen notun (yazıcının tekrar
  /// artırdığı tercih) tavanı — kullanıcının yazdığı notun (API `EXPLICIT_NOTE_WEIGHT`
  /// = 5) altında kalır; beş aynı seçim kullanıcının cümlesini geçemez.
  weight: { min: 0, max: 100, default: 1, machineMax: 4 },
  /// ANLA adımının dosyaya aldığı not sayısı (`dossierSchema.brand.memoryNotes` tavanı).
  dossierNotes: 10,
} as const;

const noteTextSchema = z.string().trim().min(1).max(BRAND_MEMORY_LIMITS.text);

/**
 * Not EKLE (`POST /brand/memory`, sohbet aracı). `source` sunucunun (sayfa →
 * USER, sohbet → AGENT); `productionId`/`creativeId` notun dayanağı (o kiracıda
 * var olduğu doğrulanır). `expiresAt` geçici notlar için ("bu ay kampanya dili").
 */
export const brandMemoryNoteCreateSchema = z.object({
  kind: brandMemoryKindSchema,
  text: noteTextSchema,
  pinned: z.boolean().optional(),
  productionId: z.uuid().optional(),
  creativeId: z.uuid().optional(),
  expiresAt: z.iso.datetime().optional(),
});
export type BrandMemoryNoteCreateInput = z.infer<typeof brandMemoryNoteCreateSchema>;

/// Not DÜZENLE (`PATCH /brand/memory/:id`) — sabitle/çöz, metni ya da türü düzelt, süreyi değiştir.
export const brandMemoryNoteUpdateSchema = z
  .object({
    kind: brandMemoryKindSchema.optional(),
    text: noteTextSchema.optional(),
    pinned: z.boolean().optional(),
    weight: z
      .number()
      .int()
      .min(BRAND_MEMORY_LIMITS.weight.min)
      .max(BRAND_MEMORY_LIMITS.weight.max)
      .optional(),
    expiresAt: z.iso.datetime().nullable().optional(),
  })
  .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
    message: 'En az bir alan değişmeli.',
  });
export type BrandMemoryNoteUpdateInput = z.infer<typeof brandMemoryNoteUpdateSchema>;

export const brandMemoryListQuerySchema = listQuerySchema.extend({
  kind: brandMemoryKindSchema.optional(),
  source: brandMemorySourceSchema.optional(),
  pinned: boolParam.optional(),
  /// Süresi dolmuş notları da göster (varsayılan gizli).
  includeExpired: boolParam.optional(),
  q: z.string().trim().min(1).max(100).optional(),
});
export type BrandMemoryListQuery = z.infer<typeof brandMemoryListQuerySchema>;

export interface BrandMemoryNoteView {
  id: string;
  kind: BrandMemoryKind;
  text: string;
  source: BrandMemorySource;
  productionId: string | null;
  creativeId: string | null;
  pinned: boolean;
  weight: number;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Dosyaya köprü — ANLA adımının okuduğu biçim
// ═══════════════════════════════════════════════════════════════════════════

/// Seçimde okunan alanlar — DB satırı da görünüm de bu şekle uyar.
export interface BrandMemoryNoteLike {
  kind: BrandMemoryKind;
  text: string;
  pinned: boolean;
  weight: number;
  createdAt: Date | string;
  expiresAt: Date | string | null;
  /// Verilirse prodüksiyon teslim kaydı (`LEARNING` + `PRODUCTION`) prompt'a girmez.
  source?: BrandMemorySource;
}

/// Kesin sınırlar — seçimde ağırlık/yenilikten ÖNCE: kural, kaçın ve reddedilen fikir.
const STRICT_NOTE_KINDS: ReadonlySet<BrandMemoryKind> = new Set(['RULE', 'AVOID', 'REJECTED']);

const time = (value: Date | string): number =>
  value instanceof Date ? value.getTime() : Date.parse(value);

/**
 * Dosyaya girecek notlar — süresi dolmuş düşer; sıra: sabitlenmiş, kesin
 * sınırlar (KURAL, KAÇIN, REDDEDİLEN), ağırlık, yenilik. `limit` varsayılan
 * `BRAND_MEMORY_LIMITS.dossierNotes`.
 *
 * 🔴 REDDEDİLEN kesin sınır (denetim bulgusu, 2 Eki 2026): ağırlığı 1, her
 * teslimin "«…» teslim edildi" kaydı ve her fikir seçiminin tercihi de 1 ve
 * daha yeni — müşterinin reddettiği fikirler birkaç prodüksiyonda prompt'tan
 * düşüyor, yazar aynı fikri yeniden öneriyordu. Teslim kaydı (`LEARNING`,
 * kaynak `PRODUCTION`) bir günlük satırı, kural değil: kaynak bilindiğinde
 * prompt'a hiç girmez (Marka sayfasında görünür).
 */
export function selectBrandMemoryNotes<T extends BrandMemoryNoteLike>(
  notes: readonly T[],
  now: Date,
  limit: number = BRAND_MEMORY_LIMITS.dossierNotes,
): T[] {
  const strict = (note: T) => (STRICT_NOTE_KINDS.has(note.kind) ? 1 : 0);
  return notes
    .filter((note) => note.expiresAt === null || time(note.expiresAt) > now.getTime())
    .filter((note) => !(note.kind === 'LEARNING' && note.source === 'PRODUCTION'))
    .sort(
      (a, b) =>
        Number(b.pinned) - Number(a.pinned) ||
        strict(b) - strict(a) ||
        b.weight - a.weight ||
        time(b.createdAt) - time(a.createdAt),
    )
    .slice(0, Math.max(0, limit));
}

/// Notun dosyadaki satırı — türü önekte, `dossierSchema.brand.memoryNotes` uzunluk tavanında.
const NOTE_PREFIX: Readonly<Record<BrandMemoryKind, string>> = {
  PREFERENCE: 'Prefers',
  REJECTED: 'Rejected',
  WINNER: 'Winner',
  RULE: 'Rule',
  AVOID: 'Avoid',
  LEARNING: 'Learned',
};

const clip = (text: string, max: number): string =>
  text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;

/**
 * Marka kiti + hafıza → ürün dosyasının `brand` bloğu (ANLA adımı; worker
 * dosyayı yazarken kendi çıkarımıyla BİRLEŞTİRİR — kitin dolu alanı kazanır).
 * Uzunluklar `dossierSchema.brand` tavanında kırpılır. Font: başlık fontunun
 * aile adı. Kurallar: "Do: …" / "Avoid: …" satırları.
 */
export function brandDossierBrand(
  kit: BrandKit | null,
  notes: readonly BrandMemoryNoteLike[],
  now: Date,
): ProductionDossier['brand'] {
  const font = kit?.fonts.display ?? kit?.fonts.body ?? null;
  const colorOrder: readonly BrandColorRole[] = [
    'primary',
    'secondary',
    'accent',
    'background',
    'text',
  ];
  const colors = [...(kit?.colors ?? [])]
    .sort((a, b) => colorOrder.indexOf(a.role) - colorOrder.indexOf(b.role))
    .map((color) => `${color.hex} (${color.role})`)
    .slice(0, 6);
  const rules = [
    ...(kit?.voiceDo ?? []).map((rule) => `Do: ${rule}`),
    ...(kit?.voiceDont ?? []).map((rule) => `Avoid: ${rule}`),
  ]
    .map((rule) => clip(rule, 200))
    .slice(0, 10);
  return {
    name: kit?.name ? clip(kit.name, 80) : null,
    tone: kit?.tone ? clip(kit.tone, 160) : null,
    colors,
    font: font ? (BRAND_FONTS.find((item) => item.id === font)?.family ?? null) : null,
    rules,
    memoryNotes: selectBrandMemoryNotes(notes, now).map((note) =>
      clip(`${NOTE_PREFIX[note.kind]}: ${note.text}`, 300),
    ),
  };
}
